<template>
  <div class="countdown-container">
    <div class="countdown-content">
      <!-- Rimosso il blocco di testo (Titolo + Descrizione) fuori dal riquadro -->

      <div class="premium-hero-box">
        <div class="promo-header">
          <span class="giveaway-badge">{{ t('newsletter.subtitle') }}</span>
          <h2>{{ t('newsletter.title') }}</h2>
        </div>
        
        <p class="hero-description">{{ t('newsletter.text') }}</p>

        <!-- Newsletter Form -->
        <div v-if="!isSubscribed" class="newsletter-form-container">
          <div class="form-group mb-2">
            <input 
              id="newsletter-email"
              name="email"
              v-model.trim="email" 
              type="email" 
              autocomplete="email"
              class="premium-input" 
              :class="{ 'input-error': (showErrors && (!email || !isEmailValid)) || serverError }"
              :placeholder="t('newsletter.placeholder')"
              :disabled="isSubmitting"
            />
            <p v-if="showErrors && !email" class="error-text">{{ t('newsletter.error.email_required') }}</p>
            <p v-else-if="showErrors && !isEmailValid" class="error-text">{{ t('newsletter.error.email_invalid') }}</p>
            <p v-else-if="serverError" class="error-text">{{ t(serverError) }}</p>
          </div>
          
          <div class="mb-3 text-start">
            <div class="form-check custom-checkbox">
              <input 
                v-model="consent" 
                type="checkbox" 
                class="form-check-input" 
                id="consentCheckbox"
                :class="{ 'checkbox-error': showErrors && !consent }"
                :disabled="isSubmitting || isAlreadySubscribed"
              />
              <label class="form-check-label" for="consentCheckbox">
                {{ t('newsletter.consent', { privacy: '' }) }}
                <router-link to="/privacy" class="privacy-link">{{ t('newsletter.privacy_link') }}</router-link>.
              </label>
            </div>
            <p v-if="showErrors && !consent" class="error-text mt-1">{{ t('newsletter.error.consent_required') }}</p>
          </div>

          <button 
            @click="subscribe" 
            class="premium-cta w-100" 
            :disabled="isSubmitting || isAlreadySubscribed"
          >
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
            {{ t('newsletter.button') }}
          </button>

          <!-- Legal notice for reCAPTCHA -->
          <div class="recaptcha-notice" v-html="t('form.recaptcha_notice', { 
            privacy: `<a href='https://policies.google.com/privacy' target='_blank'>${t('form.google_privacy')}</a>`, 
            terms: `<a href='https://policies.google.com/terms' target='_blank'>${t('form.google_terms')}</a>` 
          })"></div>
        </div>

        <!-- Success Message -->
        <div v-else class="newsletter-success-container">
          <div class="success-icon mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" class="bi bi-check2-circle text-teal" viewBox="0 0 16 16">
              <path d="M2.5 8a5.5 5.5 0 0 1 8.25-4.764.5.5 0 0 0 .5-.866A6.5 6.5 0 1 0 14.5 8a.5.5 0 0 0-1 0 5.5 5.5 0 1 1-11 0z"/>
              <path d="M15.354 3.354a.5.5 0 0 0-.708-.708L8 9.293 5.354 6.646a.5.5 0 1 0-.708.708l3 3a.5.5 0 0 0 .708 0l7-7z"/>
            </svg>
          </div>
          <p class="success-msg">{{ t('newsletter.success') }}</p>
        </div>

        <!-- Timer integrato nel box premium -->
        <div class="integrated-timer">
          <div class="timer-unit">
            <span class="unit-value">{{ days }}</span>
            <span class="unit-label">{{ t('countdown.days') }}</span>
          </div>
          <div class="timer-sep">:</div>
          <div class="timer-unit">
            <span class="unit-value">{{ hours }}</span>
            <span class="unit-label">{{ t('countdown.hours') }}</span>
          </div>
          <div class="timer-sep">:</div>
          <div class="timer-unit">
            <span class="unit-value">{{ minutes }}</span>
            <span class="unit-label">{{ t('countdown.minutes') }}</span>
          </div>
          <div class="timer-sep">:</div>
          <div class="timer-unit">
            <span class="unit-value">{{ seconds }}</span>
            <span class="unit-label">{{ t('countdown.seconds') }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Animated background elements (più sottili per estetica minimal) -->
    <div class="bg-glow bg-glow-1"></div>
    <div class="bg-glow bg-glow-2"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useLanguage } from '@/composables/useLanguage'
import { useConfigStore } from '@/stores/configStore'
import { useAuthStore } from '@/stores/authStore'
import apiClient from '@/services/api'

const { t } = useLanguage()
const configStore = useConfigStore()
const authStore = useAuthStore()

const email = ref('')
const consent = ref(false)
const isSubmitting = ref(false)
const isSubscribed = ref(false)
const isAlreadySubscribed = ref(false)
const showErrors = ref(false)
const serverError = ref('')

const isEmailValid = computed(() => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email.value)
})

// Reset server error when email changes
watch(email, () => {
  if (isAlreadySubscribed.value) {
    isAlreadySubscribed.value = false
    serverError.value = ''
  }
})

const days = ref('00')
const hours = ref('00')
const minutes = ref('00')
const seconds = ref('00')

const subscribe = async () => {
  showErrors.value = true
  serverError.value = ''
  
  if (!email.value || !isEmailValid.value || !consent.value) return
  
  isSubmitting.value = true
  try {
    // Generate reCAPTCHA Enterprise token
    let recaptchaToken = ''
    
    // Funzione helper per attendere il caricamento di grecaptcha se necessario
    const waitForRecaptcha = () => {
      return new Promise((resolve) => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          resolve(true)
          return
        }
        
        let attempts = 0
        const interval = setInterval(() => {
          attempts++
          if (window.grecaptcha && window.grecaptcha.enterprise) {
            clearInterval(interval)
            resolve(true)
          } else if (attempts > 50) { // 5 secondi di timeout
            clearInterval(interval)
            resolve(false)
          }
        }, 100)
      })
    }

    const isRecaptchaReady = await waitForRecaptcha()

    if (isRecaptchaReady && configStore.recaptchaSiteKey) {
      recaptchaToken = await new Promise((resolve) => {
        window.grecaptcha.enterprise.ready(async () => {
          try {
            const token = await window.grecaptcha.enterprise.execute(configStore.recaptchaSiteKey, { action: 'newsletter_subscription' })
            resolve(token)
          } catch (e) {
            console.error('[RECAPTCHA] Execute error:', e)
            resolve('')
          }
        })
      })
    }

    if (!recaptchaToken) {
      console.warn('[RECAPTCHA] Token non generato, procedo comunque (il backend potrebbe rifiutare)')
    }

    await apiClient.post('/newsletter/subscribe', {
      email: email.value,
      isPrivacyAccepted: true,
      recaptchaToken: recaptchaToken
    })
    isSubscribed.value = true
  } catch (error) {
    if (error.response?.status === 409) {
      isAlreadySubscribed.value = true
      serverError.value = 'newsletter.already_subscribed'
    } else if (error.response?.status === 503) {
      serverError.value = 'newsletter.error.service_unavailable'
    } else {
      serverError.value = 'newsletter.error.generic'
      console.error('[NEWSLETTER] Error subscribing:', error)
    }
  } finally {
    isSubmitting.value = false
  }
}

// Data di lancio dinamica dall'API o fallback
const getLaunchDate = () => {
  if (configStore.countdownEndDate) {
    return new Date(configStore.countdownEndDate)
  }
  return new Date('2026-07-12T00:00:00')
}

let launchDate = getLaunchDate()
let timerInterval = null

const updateTimer = () => {
  const now = new Date().getTime()
  const distance = launchDate.getTime() - now

  if (distance < 0) {
    clearInterval(timerInterval)
    return
  }

  days.value = Math.floor(distance / (1000 * 60 * 60 * 24)).toString().padStart(2, '0')
  hours.value = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)).toString().padStart(2, '0')
  minutes.value = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)).toString().padStart(2, '0')
  seconds.value = Math.floor((distance % (1000 * 60)) / 1000).toString().padStart(2, '0')
}

onMounted(() => {
  // Ricalcola la data di lancio al mount per sicurezza
  launchDate = getLaunchDate()
  updateTimer()
  timerInterval = setInterval(updateTimer, 1000)
})

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval)
})
</script>

<style scoped>
.countdown-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: #000000;
  padding: 1rem;
  text-align: center;
  position: relative;
  width: 100%;
  height: 100%; /* Occupa tutto lo spazio di .main-content */
  overflow: hidden;
}

.countdown-content {
  position: relative;
  z-index: 10;
  max-width: 520px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
}

.premium-hero-box {
  background: linear-gradient(135deg, #0a1c1d 0%, #000000 100%); /* Torna scuro */
  border: 2px solid rgba(229, 229, 216, 0.2);
  padding: 35px 30px; /* Più compatto */
  border-radius: 12px;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.3); /* Ombra più marcata per staccare dal bianco */
  width: 100%;
  position: relative;
}

.promo-header {
  margin-bottom: 0;
}

.giveaway-badge {
  color: var(--bright-teal);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  margin-bottom: 8px;
  display: block;
}

.premium-hero-box h2 {
  font-size: 26px; /* Più raffinato */
  font-weight: 900;
  margin: 0;
  color: #e5e5d8; /* Torna chiaro */
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 15px;
}

.hero-description {
  font-size: 0.85rem; /* Ridotto per eleganza */
  color: #e5e5d8; /* Torna chiaro */
  margin-bottom: 25px;
  line-height: 1.6;
  opacity: 0.7;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}

.badges-container {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 30px;
  flex-wrap: wrap; /* Permette la disposizione su due righe */
  max-width: 400px; /* Forza il wrapping per maggiore compattezza */
  margin-left: auto;
  margin-right: auto;
}

.badge-item {
  background: transparent;
  padding: 5px 12px;
  border-radius: 2px;
  font-size: 9px; /* Ancora più piccolo e "technical" */
  font-weight: 700;
  color: #e5e5d8;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  border: 1px solid rgba(229, 229, 216, 0.2);
  white-space: nowrap;
}

.premium-cta {
  background: var(--bright-teal);
  color: #000;
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 15px 45px; /* Più slanciato */
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 900;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.premium-cta:hover {
  transform: translateY(-2px);
  filter: brightness(1.1);
  box-shadow: 0 10px 20px rgba(44, 188, 196, 0.2);
}

/* Newsletter Form Styles */
.newsletter-form-container {
  margin-bottom: 20px;
  width: 100%;
}

.premium-input {
  width: 100%;
  background: rgba(229, 229, 216, 0.05);
  border: 2px solid var(--bright-teal);
  border-radius: 4px;
  padding: 15px 20px;
  color: #e5e5d8;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.premium-input:hover {
  background: rgba(44, 188, 196, 0.1);
  box-shadow: 0 0 10px rgba(44, 188, 196, 0.2);
}

.premium-input:focus {
  outline: none;
  border-color: var(--bright-teal);
  background: rgba(44, 188, 196, 0.05);
  box-shadow: 0 0 15px rgba(44, 188, 196, 0.3);
}

.input-error {
  border-color: #ff4d4d !important;
  background: rgba(255, 77, 77, 0.05) !important;
}

.checkbox-error {
  border-color: #ff4d4d !important;
  box-shadow: 0 0 5px rgba(255, 77, 77, 0.3);
}

.error-text {
  color: #ff4d4d;
  font-size: 0.7rem;
  text-align: left;
  margin-top: 5px;
  margin-bottom: 0;
  font-weight: 600;
  animation: fadeIn 0.3s ease;
}

.custom-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.custom-checkbox .form-check-input {
  background-color: transparent;
  border: 2px solid var(--bright-teal);
  width: 20px;
  height: 20px;
  margin-top: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-checkbox .form-check-input:hover {
  box-shadow: 0 0 8px rgba(44, 188, 196, 0.4);
}

.custom-checkbox .form-check-input:checked {
  background-color: var(--bright-teal);
  border-color: var(--bright-teal);
}

.recaptcha-notice {
  font-size: 10px;
  color: #e5e5d8;
  opacity: 0.5;
  margin-top: 15px;
  line-height: 1.4;
  text-align: left;
}

.recaptcha-notice a {
  color: #e5e5d8;
  text-decoration: underline;
}

.custom-checkbox .form-check-label {
  color: #e5e5d8;
  font-size: 0.75rem;
  line-height: 1.4;
  opacity: 1;
  cursor: pointer;
}

.privacy-link {
  color: var(--bright-teal);
  text-decoration: underline;
  transition: opacity 0.3s ease;
}

.privacy-link:hover {
  opacity: 0.8;
}

/* Success Message Styles */
.newsletter-success-container {
  margin-bottom: 20px;
  padding: 20px;
  animation: fadeIn 0.8s ease-out;
}

.text-teal {
  color: var(--bright-teal);
}

.success-msg {
  color: #e5e5d8;
  font-size: 0.95rem;
  line-height: 1.6;
}

.logged-user-info {
  margin-bottom: 35px;
  animation: fadeIn 0.8s ease-out;
}

.user-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(44, 188, 196, 0.1);
  color: var(--bright-teal);
  padding: 6px 15px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 20px;
  border: 1px solid rgba(44, 188, 196, 0.2);
}

.status-dot {
  width: 8px;
  height: 8px;
  background-color: var(--bright-teal);
  border-radius: 50%;
  box-shadow: 0 0 10px var(--bright-teal);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
}

.user-welcome {
  color: #e5e5d8;
  font-size: 1.1rem;
  margin-bottom: 10px;
}

.user-welcome strong {
  color: var(--bright-teal);
}

.registration-success-msg {
  color: #e5e5d8;
  font-size: 0.85rem;
  opacity: 0.6;
  line-height: 1.6;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.integrated-timer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding-top: 20px; /* Ridotto */
  border-top: 1px solid rgba(229, 229, 216, 0.05); /* Torna scuro */
}

.timer-unit {
  display: flex;
  flex-direction: column;
  min-width: 70px;
}

.unit-value {
  font-size: 2.2rem;
  font-weight: 900;
  color: var(--bright-teal); /* Torna Bright Teal */
  line-height: 1;
  font-family: 'Inter', sans-serif;
}

.unit-label {
  font-size: 0.6rem;
  text-transform: uppercase;
  color: #e5e5d8;
  letter-spacing: 1px;
  margin-top: 8px;
  font-weight: 700;
  opacity: 0.4;
}

.timer-sep {
  font-size: 1.5rem;
  color: rgba(229, 229, 216, 0.1);
  margin-top: -15px;
}

.exclusivity-notice {
  font-size: 0.75rem;
  color: #e5e5d8;
  margin-top: 30px;
  opacity: 0.4;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* Background Effects - Adattati per light mode */
.bg-glow {
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  filter: blur(120px);
  z-index: 1;
  opacity: 0.05;
}

.bg-glow-1 {
  background: var(--teal);
  top: -200px;
  right: -100px;
}

.bg-glow-2 {
  background: var(--dark-teal);
  bottom: -200px;
  left: -100px;
}

@media (max-width: 768px) {
  .countdown-container {
    padding: 1rem;
  }
  
  .premium-hero-box {
    padding: 25px 20px;
  }
  
  .premium-hero-box h2 { font-size: 22px; }
  .unit-value { font-size: 1.8rem; }
  .timer-unit { min-width: 50px; }
  .premium-cta { padding: 12px 20px; margin-bottom: 20px; }
  .hero-description { font-size: 0.8rem; margin-bottom: 20px; }
  .integrated-timer { padding-top: 20px; }
}

@media (max-height: 750px) {
  .premium-hero-box {
    padding: 20px 20px;
  }
  .premium-hero-box h2 { font-size: 20px; margin-bottom: 8px; }
  .hero-description { font-size: 0.75rem; margin-bottom: 15px; }
  .newsletter-form-container { margin-bottom: 15px; }
  .premium-cta { padding: 10px 20px; margin-bottom: 12px; font-size: 0.75rem; }
  .integrated-timer { padding-top: 15px; }
  .unit-value { font-size: 1.5rem; }
  .unit-label { font-size: 0.5rem; margin-top: 4px; }
  .newsletter-success-container { margin-bottom: 15px; padding: 10px; }
}

@media (max-height: 680px) {
  .countdown-container {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .premium-hero-box {
    padding: 1rem 1.2rem;
  }
  .premium-hero-box h2 { font-size: 18px; margin-bottom: 5px; }
  .hero-description { font-size: 0.7rem; margin-bottom: 10px; line-height: 1.3; }
  .newsletter-form-container { margin-bottom: 10px; }
  .custom-checkbox { margin-bottom: 10px !important; }
  .premium-cta { padding: 8px 15px; margin-bottom: 10px; font-size: 0.7rem; }
  .integrated-timer { padding-top: 10px; gap: 10px; }
  .unit-value { font-size: 1.2rem; }
  .timer-unit { min-width: 40px; }
  .timer-sep { font-size: 1rem; margin-top: -10px; }
  .giveaway-badge { font-size: 9px; letter-spacing: 2px; margin-bottom: 5px; }
}
@media (max-height: 550px) {
  .countdown-container {
    height: auto !important;
    min-height: 0 !important;
    display: block !important;
    padding-top: 1rem !important;
    padding-bottom: 2rem !important;
    overflow: visible !important;
    flex: none !important;
  }
}
</style>
